class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.availabilityTriggerKeywords = ['gauge', 'grip-size', 'size'];

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static showMobileLoader() {
    // TP Addition
    const mobileLoader = document.getElementById('FacetFiltersMobileLoader');
    if (mobileLoader) {
      mobileLoader.hidden = false;
      const activeFacetsWrapper = mobileLoader.closest('.tp-facet-filter-form__active-facets-wrapper');
      if (activeFacetsWrapper) {
        activeFacetsWrapper.removeAttribute('hidden');
      }
    }
  }

  static hideMobileLoader() {
    // TP Addition
    const mobileLoader = document.getElementById('FacetFiltersMobileLoader');
    if (mobileLoader) {
      mobileLoader.hidden = true;
    }
  }

  // TP-Adjustment(TPSR-129): Add trackEvents parameter to disable tracking events
  static renderPage(searchParams, event, updateURLHash = true, trackEvents = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    
    // TP Addition
    const isMobileDrawerOpen = Boolean(
      document.querySelector('menu-drawer details.disclosure-has-popup[open]') 
    );
    if (isMobileDrawerOpen) {
      FacetFiltersForm.showMobileLoader();
    } else {
      FacetFiltersForm.hideMobileLoader();
    }
    document.querySelectorAll('facet-filters-form').forEach((form) => {
      if (form.querySelector('#FacetFiltersFormMobile')) {
        if (isMobileDrawerOpen) {
          form.classList.add('tp-mobile-facets-loading');
        }
          return;
      }

      form.classList.add('loading');
    });
    document.querySelectorAll('.facets-container').forEach((container) => container.classList.add('loading'));
    // TP Addition End
    
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    // TP-Adjustment: Push `filter` and `sort` events.
    if (!trackEvents) {
      return;
    }
    
    const parsedSearchParams = new URLSearchParams(window.location.href);

    const appliedFilters = {}
    let appliedSorting = null;

    for (const [key, value] of parsedSearchParams) {
      if (value && key.includes("filter")) {
        // TPSR-1457: Extract only the important part of the filter value. E.g.: Women_5519 only 5519
        const importantPartOfValue = value.includes("_") ? value.split("_")[1] : value;

        appliedFilters[`${key}`] ? appliedFilters[`${key}`].push(importantPartOfValue) : appliedFilters[`${key}`] = [importantPartOfValue];
      }

      if (value && key.includes("sort_by")) {
        appliedSorting = value
      }
    }

    const formattedFilters = Object.fromEntries(
      Object.entries(appliedFilters).map(([key, value]) => [
        key,
        Array.isArray(value) ? value.join(",") : value
      ])
    );

    const formattedFiltersString = JSON.stringify(formattedFilters);
    const filtersHaveChanged = formattedFiltersString !== FacetFiltersForm.selectedFiltersString;

    if (filtersHaveChanged && Object.keys(formattedFilters).length > 0) {
      const filterPayload = {
        event: 'filter',
        event_id: window.tracking.generateEventId(),
        filter: formattedFilters,
      }

      FacetFiltersForm.selectedFiltersString = formattedFiltersString;

      window.tracking.pushToDataLayer(filterPayload);
    }

    // Only trigger sort event if sorting actually changed
    // Initialize on first load without triggering event
    if (appliedSorting) {
      if (FacetFiltersForm.selectedSortOptionString === null) {
        // First load: just store the value without triggering event
        FacetFiltersForm.selectedSortOptionString = appliedSorting;
      } else if (FacetFiltersForm.selectedSortOptionString !== appliedSorting) {
        // Sorting actually changed: trigger event
        const sortingPayload = {
          event: 'sort',
          event_id: window.tracking.generateEventId(),
          sort_value: appliedSorting
        }

        FacetFiltersForm.selectedSortOptionString = appliedSorting;

        window.tracking.pushToDataLayer(sortingPayload);
      }
    }
    // TP-Adjustment End.
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderCategoryChips(html); // TP-Addition (TPSR-228)
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.renderCategoryChips(html); // TP-Addition (TPSR-228)
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;


    // TP Addition - TPSR-1113
    document.dispatchEvent(new CustomEvent('tp:product-grid:rerendered'));

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const productCountElement = parsedHTML.getElementById('ProductCount');

    if (productCountElement) {
      const count = productCountElement.innerHTML;
      const container = document.getElementById('ProductCount');
      const containerDesktop = document.getElementById('ProductCountDesktop');
      if (container) {
        container.innerHTML = count;
        container.classList.remove('loading');
      }
      if (containerDesktop) {
        containerDesktop.innerHTML = count;
        containerDesktop.classList.remove('loading'); 
      }

      // tp custom: Update mobile apply button counts
      const productCountText = productCountElement.textContent.trim();
      const countMatch = productCountText.match(/(\d+)/);
      if (countMatch) {
        const productCount = countMatch[1];
        document.querySelectorAll('.tp-facet-filter-form__mobile-apply-button .tp-button__text').forEach((button) => {
          const currentText = button.textContent;
          const applyText = currentText.split('(')[0].trim();
          button.textContent = `${applyText} (${productCount})`;
        });
      }

      const mobileApplyButton = parsedHTML.getElementById('mobile-facet-main-apply-button-wrapper');
      if (mobileApplyButton) {
        document.getElementById('mobile-facet-main-apply-button-wrapper').innerHTML = mobileApplyButton.innerHTML;
      }
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    // TP Addition
    document.querySelectorAll('facet-filters-form').forEach((form) => {
      form.classList.remove('loading');
      form.classList.remove('tp-mobile-facets-loading');
    });
    document.querySelectorAll('.facets-container').forEach((container) => container.classList.remove('loading'));
    document.querySelectorAll('.tp-facet-filter-form__mobile-apply-button').forEach((button) => button.classList.remove('loading'));
    FacetFiltersForm.hideMobileLoader();
    // TP Addition End
  }

  // TP-Addition (TPSR-228)
  static renderCategoryChips(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    
    const categoryChipsUL = parsedHTML.getElementById('CategoryChips');
    const existingCategoryChipsUL = document.getElementById('CategoryChips');

    if (existingCategoryChipsUL && categoryChipsUL) {
      // replace existing category chips
      existingCategoryChipsUL.replaceWith(categoryChipsUL);
    } else if (categoryChipsUL) {
      // add new category chips
      document.querySelector('.tp-product-grid__header').prepend(categoryChipsUL);
    } else {
      // remove previously existing category chips
      existingCategoryChipsUL.remove();
    }
  }
  // TP-Addition End (TPSR-228)

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
        // TP Custom: Dispatch event for multiselect search components to reapply filtering
        document.dispatchEvent(new CustomEvent('tp:facets:rerendered'));
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    document.dispatchEvent(new CustomEvent('facets:reloaded'));

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetActiveFacets = document.querySelector(selector);
      if (!targetActiveFacets) return;

      targetActiveFacets.innerHTML = activeFacetsElement.innerHTML;

      if (activeFacetsElement.dataset.hasActive !== undefined) {
        targetActiveFacets.dataset.hasActive = activeFacetsElement.dataset.hasActive;
        const activeFacetsWrapper = targetActiveFacets.closest('.tp-facet-filter-form__active-facets-wrapper');
        if (activeFacetsWrapper) {
          const hasActive = activeFacetsElement.dataset.hasActive === 'true';
          if (hasActive) {
            activeFacetsWrapper.removeAttribute('hidden');
          } else {
            activeFacetsWrapper.setAttribute('hidden', '');
          }
        }
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    /* TP-Addition (TPSR-1789):
      Combine Filter logic by filter name's
      - only activate if filtering for unavailable products is not already active
      - on remove, remove available filter if unavailable filter is not also active
    */
    const availibilityFilter = event.target.closest('form').querySelector('input[name="filter.v.availability"][value="1"]');
    const filterAttributeName = event.target.getAttribute('name');
    const keywordsFound = this.availabilityTriggerKeywords.find(key => filterAttributeName?.includes(key));
    const checkedUnavailibilityFilters = event.target.closest('form').querySelector('input[name="filter.v.availability"][value="0"]:checked') !== null;

    if (availibilityFilter && !checkedUnavailibilityFilters && keywordsFound) {
      if (event.target.checked) {
        availibilityFilter.checked = true;
      } else {
        const anyKeywordFilterActive = this.availabilityTriggerKeywords.some(key => {
          const inputs = event.target.closest('form').querySelectorAll(`input[name*="${key}"]:checked`);
          return inputs.length > 0;
        });

        if (!anyKeywordFilterActive) {
          availibilityFilter.checked = false;
        }
      }
    }
    // TP-Addition End

    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    let url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    // TP-Addition (TPSR-1789): Sync availability filter on facet-remove
    const currentParams = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams(url);
    const removedKeys = [];

    for (const [key, value] of currentParams) {
      if (!newParams.getAll(key).includes(value)) {
        removedKeys.push(key);
      }
    }

    const wasRemovingAvailabilityTrigger = removedKeys.some(key =>
      this.availabilityTriggerKeywords.some(kw => key.includes(kw))
    );

    const unavailabilityActive = newParams.getAll('filter.v.availability').includes('0');

    if (wasRemovingAvailabilityTrigger && !unavailabilityActive) {
      let anyKeywordFilterStillActive = false;

      newParams.forEach((value, key) => {
        if (this.availabilityTriggerKeywords.some(kw => key.includes(kw))) {
          anyKeywordFilterStillActive = true;
        }
      });

      if (!anyKeywordFilterStillActive) {
        newParams.delete('filter.v.availability');
        url = newParams.toString();
      }
    }
    // TP-Addition End

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
// TP-Adjustment: Keep track of selected filters and sort option to only run certain logic when they changed.
FacetFiltersForm.selectedFiltersString = null;
FacetFiltersForm.selectedSortOptionString = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// Single-select filter functionality
class SingleSelectFilters {
  constructor() {
    this.previouslySelected = new Map();
    this.init();
    this.listenForFilterReload();
  }

  init() {
    this.attachEventListeners();
  }

  attachEventListeners() {
    document.addEventListener('click', (event) => {
      const radioInput = event.target.closest('input[type="radio"][data-single-select="true"]');
      // tp addition: handle checkbox inputs for single-select filters
      const checkboxInput = event.target.closest('input[type="checkbox"][data-single-select="true"]');
      if (radioInput) {
        this.handleSingleSelectClick(event, radioInput);
      } else if (checkboxInput) {
        this.handleSingleSelectClick(event, checkboxInput);
      }
    });
  }

  handleSingleSelectClick(event, input) {
    const groupName = input.name;
    const currentValue = input.value;
    const wasAlreadySelected = this.previouslySelected.get(groupName) === currentValue;
    const form = input.closest('form');

    // tp addition: helper to sync all inputs with the same name (for purchasinginfo_100405 filter)
    const syncAllInputs = (checked) => {
      if (form) {
        const allInputsWithSameName = form.querySelectorAll(`input[name="${groupName}"][data-single-select="true"]`);
        allInputsWithSameName.forEach((inp) => {
          inp.checked = checked;
        });
      }
    };
 
    syncAllInputs(input.checked);

    if (wasAlreadySelected) {
      // If the same option was clicked again, deselect it
      input.checked = false;
      syncAllInputs(false);
      this.previouslySelected.delete(groupName);
      // Trigger form submission to update the page
      if (form) {
        const facetForm = form.closest('facet-filters-form');
        if (facetForm) {
          const searchParams = facetForm.createSearchParams(form);
          facetForm.onSubmitForm(searchParams, event);
        }
      }
    } else {
      // Remember the newly selected value
      this.previouslySelected.set(groupName, currentValue);
    }
  }

  listenForFilterReload() {
    document.addEventListener('facets:reloaded', () => {
      // tp addition 
      this.previouslySelected.clear();
      document.querySelectorAll('input[data-single-select="true"]:checked').forEach((input) => {
        this.previouslySelected.set(input.name, input.value);
      });
      // tp addition end
    });
  }
}

// Initialize single-select functionality
new SingleSelectFilters();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
